// 攝影機設定 →「不通知區」（docs/mute-zones.md §11-5）：每塊一列（名稱、種類、擋了幾則、狀態、建立者）；
// 自己建的或主人可以改名稱 / 種類 / 期限、刪除。
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MUTE_LABELS, deleteMuteZone, listMuteZones, updateMuteZone, zoneStatus } from '../../events/muteZones';
import type { MuteLabel, MuteZone } from '../../events/muteZones';
import { SectionCard } from './widgets';

export function MuteZonesSection({ deviceId, isOwner }: { deviceId: string; isOwner: boolean }) {
  const { t } = useTranslation();
  const [zones, setZones] = useState<MuteZone[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const reload = useCallback(() => {
    void listMuteZones(deviceId).then(setZones).catch(() => setZones([]));
  }, [deviceId]);
  useEffect(reload, [reload]);
  const now = Math.floor(Date.now() / 1000);

  const statusText = (z: MuteZone) => {
    const s = zoneStatus(z, now);
    return s.kind === 'off' ? t('muteZones.statusOff') : s.kind === 'expired' ? t('muteZones.statusExpired')
      : s.kind === 'always' ? t('muteZones.statusAlways') : t('muteZones.daysLeft', { count: s.days });
  };

  return (
    <SectionCard title={t('muteZones.section')}>
      <div data-testid="mz-section">
        {zones === null && <p className="text-secondary">{t('common.loading')}</p>}
        {zones?.length === 0 && <p className="text-secondary" data-testid="mz-empty">{t('muteZones.empty')}</p>}
        {zones?.map((z) => (
          <div key={z.zone_id} className="mz-item" data-testid={`mz-item-${z.zone_id}`}>
            {editing === z.zone_id ? (
              <ZoneEdit z={z} deviceId={deviceId} onDone={() => { setEditing(null); reload(); }} />
            ) : (
              <>
                <div className="mz-item-main">
                  <strong className="mz-item-name">{z.name}</strong>
                  <span className="text-secondary">
                    {z.labels.map((l) => t(`muteZones.label.${l}`)).join(' / ')} · {t('muteZones.blocked', { count: z.hits })} · {statusText(z)} ·{' '}
                    {z.mine ? t('muteZones.byMe') : t('muteZones.byFamily')}
                  </span>
                </div>
                {(z.mine || isOwner) && (
                  <div className="mz-item-actions">
                    <button className="btn-ghost" onClick={() => setEditing(z.zone_id)} data-testid={`mz-edit-${z.zone_id}`}>{t('common.edit')}</button>
                    <button className="btn-ghost text-danger" data-testid={`mz-delete-${z.zone_id}`}
                      onClick={() => void deleteMuteZone(deviceId, z.zone_id).then(reload)}>
                      {t('muteZones.delete')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function ZoneEdit({ z, deviceId, onDone }: { z: MuteZone; deviceId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(z.name);
  const [labels, setLabels] = useState<MuteLabel[]>(z.labels);
  const [dur, setDur] = useState<'keep' | '30' | 'always'>('keep');
  const save = () =>
    void updateMuteZone(deviceId, z.zone_id, {
      name: name.trim(), labels, ...(dur === 'keep' ? {} : { expires_days: dur === 'always' ? -1 : 30 }),
    }).then(onDone);
  return (
    <div className="mz-edit" data-testid="mz-edit-form">
      <input value={name} maxLength={20} onChange={(e) => setName(e.target.value)} data-testid="mz-edit-name" />
      {MUTE_LABELS.map((l) => (
        <label key={l} className="mz-check">
          <input type="checkbox" checked={labels.includes(l)} onChange={(e) => setLabels((xs) => (e.target.checked ? [...xs, l] : xs.filter((x) => x !== l)))} />
          {t(`muteZones.label.${l}`)}
        </label>
      ))}
      <select value={dur} onChange={(e) => setDur(e.target.value as 'keep' | '30' | 'always')} data-testid="mz-edit-dur">
        <option value="keep">—</option>
        <option value="30">{t('muteZones.days30')}</option>
        <option value="always">{t('muteZones.always')}</option>
      </select>
      <button className="btn-ghost" onClick={onDone}>{t('common.cancel')}</button>
      <button className="btn-primary" disabled={!name.trim() || !labels.length} onClick={save} data-testid="mz-edit-save">{t('muteZones.save')}</button>
    </div>
  );
}
